/**
 * SOUND-690 — the Focus board's logic.
 *
 * Ben's merchant-services standup opens on the application funnel: what is
 * outstanding, and what can be collected once to clear several things at once.
 * Everything here is pure so it can be tested — this repo has no component
 * harness (SOUND-679), so any logic left inside a component is unverified.
 *
 * THE GROUPING AXIS IS THE PROCESSOR, NOT THE DBA. Measured in prod 2026-08-24:
 * Ares Enterprises has 7 open pends on ONE DBA (PUPPYTIDE) split across Kurv and
 * PayArc, both asking for documents. EVERGREEN ECOM has "Website Edit" pending
 * at Kurv AND Netevia on the same DBA. So the collapse worth showing is one
 * client + one DBA + n processors — collect the document once, clear three pends.
 */

/**
 * Application stages where a pend can no longer be acted on.
 *
 * Not cosmetic: 14 of the 47 open pends in prod sit on applications that are
 * already approved or withdrawn, the oldest raised 2026-06-16. Show everything
 * and a third of the board is work nobody can do.
 */
export const CLOSED_APP_STATUSES = ["approved", "declined", "withdrawn"];

const isClosed = (p) => CLOSED_APP_STATUSES.includes(p?.applicationStatus);

/** Whole days since a pend was raised. */
export function pendAgeDays(pend, now = Date.now()) {
  const raised = pend?.createdAt ? new Date(pend.createdAt).getTime() : NaN;
  if (Number.isNaN(raised)) return null;
  return Math.max(0, Math.floor((now - raised) / 86400000));
}

/**
 * Age tone. The thresholds come from the real distribution, which is BIMODAL:
 * 26 of 47 open pends are 14 days or younger, 21 are 30+, and NOTHING sits in
 * the 15–30 day band. So "old" is a genuine cliff here, not an arbitrary line.
 */
export function pendAgeTone(days) {
  if (days == null) return "muted";
  if (days >= 30) return "destructive";
  if (days >= 7) return "warning";
  return "muted";
}

/**
 * Split the board from what it is deliberately not showing.
 *
 * Returned as a pair rather than a filtered list because the count of what is
 * hidden has to be RENDERED. Silent truncation reads as "covered everything"
 * when it did not — the failure mode this codebase already has a rule about.
 */
export function partitionByApplicationStatus(pends = []) {
  const live = [];
  const closed = [];
  for (const p of pends) (isClosed(p) ? closed : live).push(p);
  return { live, closed };
}

/** A stable key for one client + DBA pairing. */
function groupKeyOf(pend) {
  // Keyed on the ID, labelled by the name: two clients can share a DBA name, and
  // a renamed DBA must not split its own group in half.
  return `${pend.companyId || "?"}::${pend.dbaName || ""}`;
}

/**
 * Group open pends into the board's rows: client → DBA → the processors it spans.
 *
 * Sorted so the conversation starts where it should: the group carrying the most
 * outstanding items first, then the one whose oldest item has waited longest.
 * Inside a group, oldest first.
 */
export function groupPendsForFocus(pends = [], now = Date.now()) {
  const groups = new Map();

  for (const pend of pends) {
    const key = groupKeyOf(pend);
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        companyId: pend.companyId || null,
        clientName: pend.clientName || null,
        // A missing DBA is its own bucket, not a dropped row. Only 988 of 1,008
        // applications carry a DBA edge, so this is the common case, not an edge.
        dbaName: pend.dbaName || null,
        pends: [],
      });
    }
    groups.get(key).pends.push(pend);
  }

  return [...groups.values()]
    .map((g) => {
      const sorted = [...g.pends].sort(
        (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
      );
      const processors = [...new Set(sorted.map((p) => p.processorName).filter(Boolean))];
      const applications = new Set(sorted.map((p) => p.applicationId).filter(Boolean));
      const oldest = pendAgeDays(sorted[0], now);
      return {
        ...g,
        pends: sorted,
        count: sorted.length,
        processors,
        applicationCount: applications.size,
        oldestDays: oldest,
        // The whole reason to group: the same ask outstanding at more than one
        // processor is collected once and cleared several times.
        spansProcessors: processors.length > 1,
        repeatedCategories: repeatedCategoriesIn(sorted),
      };
    })
    .sort((a, b) => b.count - a.count || (b.oldestDays ?? 0) - (a.oldestDays ?? 0));
}

/**
 * Categories appearing on MORE THAN ONE application inside a group.
 *
 * This is the actionable signal, and it is narrower than "appears twice": two
 * Document Requests on the SAME application are two different documents, but a
 * Document Request on two applications for the same DBA is very often one
 * document the processors both want.
 */
export function repeatedCategoriesIn(pends = []) {
  const appsByCategory = new Map();
  for (const p of pends) {
    if (!p.category) continue;
    if (!appsByCategory.has(p.category)) appsByCategory.set(p.category, new Set());
    appsByCategory.get(p.category).add(p.applicationId);
  }
  return [...appsByCategory.entries()]
    .filter(([, apps]) => apps.size > 1)
    .map(([category]) => category);
}

/**
 * The group header, stating the collapse in words.
 *
 * e.g. "7 open · PUPPYTIDE · Kurv + PayArc". The processor list is the point —
 * without it the group reads as one pile rather than the same ask in two places.
 */
export function describeGroup(group) {
  if (!group) return "";
  const parts = [`${group.count} open`];
  if (group.dbaName) parts.push(group.dbaName);
  if (group.processors.length) parts.push(group.processors.join(" + "));
  return parts.join(" · ");
}
